import { zodResolver } from "@hookform/resolvers/zod";
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbList,
  BreadcrumbSeparator,
} from "@hortis/ui/breadcrumb";
import { Button, IconButton } from "@hortis/ui/button";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@hortis/ui/dialog";
import styles from "@hortis/ui/dialog/dialog.module.css";
import {
  Drawer,
  DrawerContent,
  DrawerDescription,
  DrawerTitle,
} from "@hortis/ui/drawer";
import { useMediaQuery } from "@hortis/ui/hooks/media-query";
import { XClose } from "@hortis/ui/icons";
import { Skeleton } from "@hortis/ui/skeleton";
import { toast } from "@hortis/ui/sonner";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@hortis/ui/tooltip";
import { useQueryClient } from "@tanstack/react-query";
import { useNavigate, useRouteContext } from "@tanstack/react-router";
import {
  namedOperations,
  useGetCollectionSiteFromNamesQuery,
} from "generated/graphql";
import { useEffect, useRef, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import {
  TopBar,
  TopBarActionButtons,
  TopBarContent,
} from "src/components/nav-layout/topbar";
import { stopEventPropagation } from "src/features/collection/components/stop-event-propagation";
import { NewAccessionDataFields } from "src/features/create-accession/components/accession-data-form/new-accession-data-fields";
import { materialErrorMap } from "src/features/create-accession/components/add-plant-material-form/error-map";
import {
  type CreateAccessionFormSchema,
  createAccessionFormSchema,
} from "src/features/create-accession/components/schema";
import { processAccessionFormData } from "src/features/create-accession/utils/process-form-data";
import { useGqlMutation } from "src/utils/hooks/graphql";
import { twMerge } from "tailwind-merge";
import { createAccessionDocument } from "../graphql";
import { DiscardDialog } from "./accession-data-form/discard-dialog";
import { useCreateAccessionStore } from "./create-accession-store";

export function CreateAccessionDialog({ siteSlug }: { siteSlug: string }) {
  const {
    initialData,
    createAccessionDialogOpen: open,
    setCreateAccessionDialogOpen: onOpenChange,
  } = useCreateAccessionStore();
  const isDesktop = useMediaQuery("md");
  const [activeMaterial, setActiveMaterial] = useState<null | number>(null);
  const { subdomain } = useRouteContext({ from: "__root__" });
  const navigate = useNavigate();
  const [noMaterialDialogOpen, setNoMaterialDialogOpen] = useState(false);
  const [discardDialogOpen, setDiscardDialogOpen] = useState(false);
  const queryClient = useQueryClient();
  const taxonPickerRef = useRef<HTMLButtonElement | null>(null);

  const defaultValues = (): Partial<CreateAccessionFormSchema> => ({
    generateAccessionNumber: true,
    accessionDate: new Date(),
    tags: [],
    plantMaterials: [],
    materialTypeId: {
      code: "PT",
      name: "Plant",
    },
    ...initialData,
  });
  const form = useForm<CreateAccessionFormSchema>({
    defaultValues: defaultValues(),
    criteriaMode: "all",
    resolver: zodResolver(
      createAccessionFormSchema,
      { async: true, errorMap: materialErrorMap },
      { mode: "async" },
    ),
  });

  const createAccession = useGqlMutation({
    mutation: createAccessionDocument,
    onError: () => {
      toast.error("Failed to create accession");
    },
    onSuccess: (res) => {
      if (res.createAccession == null) {
        toast.error("Created accession number unknown");
      } else {
        const accessionNumber = res.createAccession.accessionNumber;
        toast.success("Accession created", {
          description: accessionNumber,
          action: {
            label: "View",
            onClick: () =>
              navigate({
                to: "/site/$siteSlug/accession/$accessionNumber",
                params: { accessionNumber, siteSlug },
              }),
          },
        });
        void queryClient.invalidateQueries({
          queryKey: [namedOperations.Query.GetAccessions],
        });
        void queryClient.invalidateQueries({
          queryKey: [namedOperations.Query.PlantMaterials],
        });
        void queryClient.invalidateQueries({
          queryKey: [namedOperations.Query.GetDashboard],
        });
        void queryClient.invalidateQueries({
          queryKey: [namedOperations.Query.MapListMaterials],
        });
        onOpenChange(false);
      }
    },
  });

  const [{ data: siteData, fetching }] = useGetCollectionSiteFromNamesQuery({
    variables: {
      organisationSubdomain: subdomain,
      collectionSiteSlug: siteSlug,
    },
  });
  const site = siteData?.org?.site;

  const handleSubmit = async (data: CreateAccessionFormSchema) => {
    if (site == null) {
      toast.error("Failed to create accession");
      return;
    }
    if (!noMaterialDialogOpen && data.plantMaterials.length === 0) {
      setNoMaterialDialogOpen(true);
      return;
    }

    await createAccession.mutateAsync({
      accession: {
        ...processAccessionFormData({
          ...data,
        }),
        collectionSiteId: site.id,
      },
    });
  };

  const handleOpenChange = (open: boolean) => {
    const accessionNumberSuffix = form.getValues("accessionNumberSuffix");
    const exogenousAccessionNumber = form.getValues("exogenousAccessionNumber");

    const accessionNumberSet =
      accessionNumberSuffix != null || exogenousAccessionNumber != null;

    if (accessionNumberSet && !discardDialogOpen) {
      setDiscardDialogOpen(true);
    } else if (
      Object.keys(form.formState.dirtyFields).length === 0 ||
      discardDialogOpen
    ) {
      onOpenChange(open);
    } else {
      setDiscardDialogOpen(true);
    }
  };

  useEffect(() => {
    if (open) {
      form.reset(defaultValues());
    } else {
      setActiveMaterial(null);
    }
    setDiscardDialogOpen(false);
    setNoMaterialDialogOpen(false);
  }, [open, form]);

  const content = (
    <TooltipProvider delayDuration={1000}>
      <TopBar className="bg-transparent">
        <TopBarContent>
          <Breadcrumb>
            <BreadcrumbList>
              <BreadcrumbItem
                data-cy="breadcrumb-site-name"
                className="hidden max-w-32 truncate md:block md:max-w-64"
              >
                {site == null && fetching ? (
                  <Skeleton className="h-4 w-24" />
                ) : site == null ? (
                  "Site"
                ) : (
                  site.name
                )}
              </BreadcrumbItem>
              <BreadcrumbSeparator className="hidden md:block" />
              <BreadcrumbItem data-cy="breadcrumb-page-title">
                Create accession
              </BreadcrumbItem>
            </BreadcrumbList>
          </Breadcrumb>
        </TopBarContent>
        <TopBarActionButtons className="items-center gap-0.5">
          <Tooltip>
            <DiscardDialog
              onDiscard={() => {
                handleOpenChange(false);
              }}
              open={discardDialogOpen}
              onOpenChange={setDiscardDialogOpen}
            >
              <TooltipTrigger asChild>
                <IconButton
                  ariaLabel="Close"
                  variant="tertiaryGray"
                  testId="close-create-accession-dialog"
                  size="sm"
                  icon={XClose}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleOpenChange(false);
                  }}
                  onFocusCapture={stopEventPropagation}
                />
              </TooltipTrigger>
            </DiscardDialog>
            <TooltipContent side="bottom" sideOffset={0}>
              Discard accession
            </TooltipContent>
          </Tooltip>
        </TopBarActionButtons>
      </TopBar>
      <FormProvider {...form}>
        <form
          onSubmit={form.handleSubmit(handleSubmit)}
          className="flex max-h-full min-h-0 flex-col items-stretch gap-6 overflow-y-auto px-4 py-6 transition-all md:px-6"
        >
          <NewAccessionDataFields
            activeMaterial={activeMaterial}
            setActiveMaterial={setActiveMaterial}
            taxonPickerRef={taxonPickerRef}
            siteSlug={siteSlug}
          />
          {activeMaterial == null && (
            <div className="flex justify-end">
              <Dialog
                open={noMaterialDialogOpen}
                onOpenChange={(open) => {
                  if (!open) {
                    setNoMaterialDialogOpen(false);
                  }
                }}
              >
                <Tooltip>
                  <TooltipTrigger asChild>
                    <DialogTrigger asChild>
                      <Button
                        variant="primary"
                        type="submit"
                        loading={
                          !noMaterialDialogOpen && form.formState.isSubmitting
                        }
                        testId="create-accession-button"
                        onFocusCapture={stopEventPropagation}
                        disabled={!open}
                      >
                        Create accession
                      </Button>
                    </DialogTrigger>
                  </TooltipTrigger>
                  <TooltipContent side="bottom" sideOffset={0}>
                    Save accession
                  </TooltipContent>
                </Tooltip>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>
                      Create accession without material?
                    </DialogTitle>
                    <DialogDescription>
                      You can add material after the accession has been created.
                    </DialogDescription>
                  </DialogHeader>
                  <DialogFooter>
                    <DialogClose asChild>
                      <Button>Cancel</Button>
                    </DialogClose>
                    <Button
                      variant="primary"
                      type="submit"
                      onClick={form.handleSubmit(handleSubmit)}
                      loading={form.formState.isSubmitting}
                      testId="create-accession-confirm-button"
                      disabled={!open}
                    >
                      Create accession
                    </Button>
                  </DialogFooter>
                </DialogContent>
              </Dialog>
            </div>
          )}
        </form>
      </FormProvider>
    </TooltipProvider>
  );

  if (isDesktop) {
    return (
      <Dialog open={open} onOpenChange={handleOpenChange}>
        <DialogContent
          className={twMerge(
            "top-12 flex max-h-[calc(100%-96px)] w-full max-w-[668px] translate-y-0 flex-col gap-0! rounded-xl bg-white p-0! [@media(min-height:953px)]:top-32 [@media(min-height:953px)]:max-h-[calc(100%-256px)]",
            styles.dialogNoY,
          )}
          onOpenAutoFocus={(e) => {
            e.stopPropagation();
            e.preventDefault();
            taxonPickerRef.current?.focus();
          }}
        >
          <DialogTitle className="sr-only">Create accession</DialogTitle>
          <DialogDescription className="sr-only">
            Create accession form
          </DialogDescription>
          {content}
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Drawer open={open} onOpenChange={handleOpenChange} dismissible={false}>
      <DrawerContent
        className="h-full max-h-full min-h-full"
        onOpenAutoFocus={(e) => {
          e.stopPropagation();
          e.preventDefault();
          taxonPickerRef.current?.focus();
        }}
      >
        <DrawerTitle className="sr-only">Create accession</DrawerTitle>
        <DrawerDescription className="sr-only">
          Create accession form
        </DrawerDescription>
        {content}
      </DrawerContent>
    </Drawer>
  );
}
